// BLOQUE 35 PARTE B — Módulo "Alquileres" en Inventario. Partidas creadas
// automáticamente al generar una OTS de un Pedido de Servicio categoría
// "alquiler". Gestión de conteo de días (inicio/fin estimado/fin real),
// progreso y estados. Fechas date-only (Bloque 17), montos completos.
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Home, Loader2, Play, Flag, Pencil, Clock, AlertTriangle, CheckCircle2, Timer } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatDate, todayAsuncion } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";

const ESTADO_META = {
  no_iniciado: { label: "No Iniciado", chip: "bg-gray-100 text-gray-600", bar: "bg-gray-300" },
  en_curso: { label: "En Curso", chip: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-400" },
  por_vencer: { label: "Por Vencer", chip: "bg-amber-100 text-amber-700", bar: "bg-amber-400" },
  vencido: { label: "Vencido", chip: "bg-red-100 text-red-700", bar: "bg-red-400" },
  finalizado: { label: "Finalizado", chip: "bg-blue-100 text-blue-700", bar: "bg-blue-400" },
};

// BLOQUE 60 — resumen de certificación por períodos: días certificados vs.
// pendientes de certificar, e historial completo de certificaciones (período,
// días, monto y OP) de la partida. Vive dentro de GestionarModal, visible
// tanto "En Curso" como "Finalizado" (se puede certificar en ambos estados).
function CertificacionResumen({ alq }) {
  const periodos = alq.certified_periods || [];
  const certificados = alq.dias_certificados_acumulados || 0;
  const pendientes = alq.dias_pendientes_certificar ?? null;
  return (
    <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 space-y-2" data-testid="alq-certificacion-resumen">
      <div className="grid grid-cols-2 gap-3 text-center">
        <div><p className="text-[10px] text-gray-500 uppercase">Días certificados</p><p className="text-base font-bold text-violet-700" data-testid="alq-dias-certificados">{certificados}d</p></div>
        <div><p className="text-[10px] text-gray-500 uppercase">Pendientes de certificar</p><p className={`text-base font-bold ${pendientes > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="alq-dias-pendientes">{pendientes ?? "—"}d</p></div>
      </div>
      {periodos.length > 0 && (
        <div className="pt-2 border-t border-violet-100 space-y-1">
          <p className="text-[10px] font-semibold text-gray-500 uppercase">Historial de certificaciones</p>
          {periodos.slice().reverse().map((p, i) => (
            <div key={p.crt_id || i} className="flex items-center justify-between text-xs bg-white/70 rounded-lg px-2 py-1.5" data-testid={`alq-periodo-cert-${i}`}>
              <span className="font-mono text-gray-600">{p.crt_number}</span>
              <span className="text-gray-500">{formatDate(p.from)} – {formatDate(p.to)}</span>
              <span className="font-semibold text-gray-700">{p.dias_efectivos}d</span>
              <span className="font-bold text-violet-700">{formatPYG(p.monto || 0)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GestionarModal({ alq, onClose, onUpdated }) {
  const [fechaInicio, setFechaInicio] = useState(alq?.fecha_inicio || todayAsuncion());
  const [fechaFinEstimada, setFechaFinEstimada] = useState(alq?.fecha_fin_estimada || alq?.alquiler_fecha_limite_ps || "");
  const [fechaFinReal, setFechaFinReal] = useState(todayAsuncion());
  const [saving, setSaving] = useState(false);
  if (!alq) return null;
  const meta = ESTADO_META[alq.estado] || ESTADO_META.no_iniciado;

  const iniciar = async () => {
    if (!fechaFinEstimada) { toast.error("Indicá una fecha estimada de fin"); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/alquileres/${alq._id}/iniciar`, { fecha_inicio: fechaInicio, fecha_fin_estimada: fechaFinEstimada }, { withCredentials: true });
      toast.success("Alquiler iniciado");
      // BLOQUE 35 fix (testing_agent iteration_129) — mantener el modal abierto
      // con los datos frescos (pasa a la vista "En Curso") en vez de cerrarlo.
      onUpdated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al iniciar"); }
    finally { setSaving(false); }
  };

  const finalizar = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/alquileres/${alq._id}/finalizar`, { fecha_fin_real: fechaFinReal }, { withCredentials: true });
      if (res.data?.exceso_dias) toast.warning(`Finalizado con ${res.data.exceso_dias} día(s) de exceso`);
      else toast.success("Alquiler finalizado");
      onUpdated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al finalizar"); }
    finally { setSaving(false); }
  };

  const guardarEdicion = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/alquileres/${alq._id}/editar`, { fecha_inicio: fechaInicio, fecha_fin_estimada: fechaFinEstimada }, { withCredentials: true });
      toast.success("Fechas actualizadas");
      onUpdated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al editar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="alq-gestionar-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Gestionar Alquiler</h3>
          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${meta.chip}`}>{meta.label}</span>
        </div>
        <div className="bg-gray-50 rounded-xl p-3 space-y-1 text-sm">
          <p><span className="text-gray-500">Partida:</span> <span className="font-mono font-semibold">{alq.number}</span></p>
          <p><span className="text-gray-500">OTS:</span> <span className="font-mono">{alq.ots_number}</span> · <span className="text-gray-500">PS:</span> <span className="font-mono">{alq.ps_number}</span></p>
          <p><span className="text-gray-500">Obra:</span> {alq.project_name || "—"} · <span className="text-gray-500">Proveedor:</span> {alq.provider_name || "—"}</p>
          <p><span className="text-gray-500">Monto total:</span> <span className="font-bold">{formatPYG(alq.monto_total)}</span>
            {alq.monto_por_dia ? <span className="text-gray-400"> ({formatPYG(alq.monto_por_dia)}/día)</span> : null}</p>
        </div>

        {alq.estado_base === "no_iniciado" ? (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha de inicio</label>
              <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="alq-fecha-inicio-input" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha estimada de fin *</label>
              <input type="date" value={fechaFinEstimada} onChange={e => setFechaFinEstimada(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="alq-fecha-fin-estimada-input" />
            </div>
            <button onClick={iniciar} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="alq-iniciar-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Iniciar Conteo
            </button>
          </>
        ) : alq.estado_base === "en_curso" ? (
          <>
            <div className="grid grid-cols-3 gap-3 text-center bg-blue-50 rounded-xl p-3">
              <div><p className="text-[10px] text-gray-500 uppercase">Transcurridos</p><p className="text-lg font-bold text-gray-900">{alq.dias_transcurridos ?? "—"}d</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Restantes</p><p className={`text-lg font-bold ${alq.dias_restantes < 0 ? "text-red-600" : "text-gray-900"}`}>{alq.dias_restantes ?? "—"}d</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">Progreso</p><p className="text-lg font-bold text-gray-900">{alq.progreso_pct ?? 0}%</p></div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
              <div className={`h-2 rounded-full ${meta.bar}`} style={{ width: `${alq.progreso_pct || 0}%` }} />
            </div>
            <p className="text-xs text-gray-500">Inicio: {formatDate(alq.fecha_inicio)} · Fin estimado: {formatDate(alq.fecha_fin_estimada)} · Hoy: {formatDate(todayAsuncion())}</p>

            <CertificacionResumen alq={alq} />

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Editar inicio</label>
                <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="alq-editar-fecha-inicio-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Editar fin estimado</label>
                <input type="date" value={fechaFinEstimada} onChange={e => setFechaFinEstimada(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="alq-editar-fecha-fin-input" />
              </div>
            </div>
            <button onClick={guardarEdicion} disabled={saving} className="w-full border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl py-2 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="alq-guardar-edicion-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />} Guardar cambios de fecha
            </button>

            <div className="pt-2 border-t border-gray-100">
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Fecha real de fin (al finalizar)</label>
              <input type="date" value={fechaFinReal} onChange={e => setFechaFinReal(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm mb-2" data-testid="alq-fecha-fin-real-input" />
              <button onClick={finalizar} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="alq-finalizar-btn">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Flag size={15} />} Finalizar Alquiler
              </button>
            </div>
          </>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-1 text-sm">
            <p className="flex items-center gap-1.5 font-semibold text-blue-800"><CheckCircle2 size={14} /> Alquiler finalizado</p>
            <p className="text-xs text-gray-600">Inicio: {formatDate(alq.fecha_inicio)} · Fin estimado: {formatDate(alq.fecha_fin_estimada)} · Fin real: {formatDate(alq.fecha_fin_real)}</p>
            <p className="text-xs font-semibold text-blue-700">Total de días efectivamente contados: {alq.dias_transcurridos ?? alq.dias_efectivos_totales ?? "—"} días</p>
            {alq.exceso_dias ? <p className="text-xs font-semibold text-amber-700">Excedió lo estimado por {alq.exceso_dias} día(s)</p> : null}
            <CertificacionResumen alq={alq} />
          </div>
        )}
        <button onClick={onClose} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 pt-1">Cerrar</button>
      </div>
    </div>
  );
}

export default function AlquileresTab() {
  const [alquileres, setAlquileres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [searchParams] = useSearchParams();
  const openNumber = searchParams.get("open") || "";

  const fetchAll = () => {
    axios.get(`${API}/alquileres`, { withCredentials: true })
      .then(r => setAlquileres(r.data || []))
      .catch(() => toast.error("Error al cargar Alquileres"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (!openNumber || !alquileres.length) return;
    const target = alquileres.find(a => a.number === openNumber);
    if (target) setSelected(target);
  }, [openNumber, alquileres]);

  // BLOQUE 35 fix (testing_agent iteration_129) — tras iniciar/editar/finalizar,
  // refrescamos la card seleccionada en el modal Y la lista de fondo, SIN cerrar
  // el modal (evita el clic extra de reabrir para continuar el flujo).
  const handleUpdated = (updated) => {
    setSelected(updated);
    setAlquileres(prev => prev.map(a => a._id === updated._id ? updated : a));
  };

  return (
    <div className="space-y-4" data-testid="inventory-alquileres-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Partidas de alquiler generadas automáticamente al emitir una Orden de Servicio (OTS) de categoría "Alquiler".</p>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : alquileres.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-100" data-testid="alq-empty-state">
          <Home size={28} className="text-gray-300 mb-2" />
          <p className="text-sm font-medium">Todavía no hay partidas de alquiler</p>
          <p className="text-xs text-gray-400 mt-1">Se crean automáticamente al generar la OTS de un Pedido de Servicio categoría "Alquiler"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="alq-cards-grid">
          {alquileres.map(a => {
            const meta = ESTADO_META[a.estado] || ESTADO_META.no_iniciado;
            return (
              <div key={a._id} className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3" data-testid={`alq-card-${a._id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-gray-500">{a.number}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.chip}`} data-testid={`alq-estado-${a._id}`}>{meta.label}</span>
                </div>
                <p className="text-sm font-semibold text-gray-900 truncate">{a.project_name || "—"}</p>
                <p className="text-xs text-gray-400 truncate">OTS {a.ots_number} · {a.provider_name || "—"}</p>
                <p className="text-sm font-bold text-gray-900">{formatPYG(a.monto_total)}</p>
                {a.estado_base !== "no_iniciado" && (
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-1.5 rounded-full ${meta.bar}`} style={{ width: `${a.progreso_pct || 0}%` }} />
                  </div>
                )}
                {a.estado_base === "en_curso" && (
                  <p className="text-[11px] text-gray-500 flex items-center gap-1"><Clock size={11} /> {a.dias_restantes >= 0 ? `Faltan ${a.dias_restantes}d` : `Vencido hace ${Math.abs(a.dias_restantes)}d`}</p>
                )}
                <button
                  onClick={() => setSelected(a)}
                  className="w-full flex items-center justify-center gap-2 border border-gray-200 hover:border-[#2E6DA4]/40 hover:bg-blue-50/50 rounded-xl py-2 text-xs font-semibold text-gray-700 transition-all"
                  data-testid={`alq-gestionar-btn-${a._id}`}
                >
                  <Timer size={13} /> Gestionar Alquiler
                </button>
              </div>
            );
          })}
        </div>
      )}
      {selected && <GestionarModal alq={selected} onClose={() => setSelected(null)} onUpdated={handleUpdated} />}
    </div>
  );
}
